/**
 * CreditMemoTimeline — read-only list of credit memos on an invoice.
 *
 * Works for both seller and buyer views: backend GET /invoices/{id}/credit-memos
 * authorizes either party. Renders empty-state when zero memos exist.
 *
 * Caller:
 *   <CreditMemoTimeline invoiceId={inv.id} compact/>
 */
import { useEffect, useState } from "react";
import { Receipt, Loader2 } from "lucide-react";
import api, { money } from "../lib/api";

export default function CreditMemoTimeline({ invoiceId, compact = false }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!invoiceId) return;
    setItems(null);
    api.get(`/invoices/${invoiceId}/credit-memos`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [invoiceId]);

  if (items === null) {
    return (
      <div className="text-[12px] text-foreground/55 inline-flex items-center gap-1.5 py-2">
        <Loader2 className="w-3 h-3 animate-spin"/>Loading credit memos…
      </div>
    );
  }
  if (items.length === 0) return null;   // hide section entirely when empty

  const totalCredited = items.reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="space-y-2" data-testid="credit-memo-timeline">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[11.5px] mono uppercase tracking-[0.15em] text-foreground/55">
          <Receipt className="w-3 h-3"/>
          Credit memos
        </div>
        <div className="text-[11.5px] text-emerald-700 mono">
          {items.length} memo{items.length === 1 ? "" : "s"} · {money(totalCredited)}
        </div>
      </div>
      <ul className="rounded-lg border border-border overflow-hidden divide-y divide-border bg-secondary/20">
        {items.map(cm => (
          <li key={cm.id} className="p-2.5 text-[12px] flex items-start gap-2.5"
              data-testid={`cm-row-${cm.code}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">
                  {cm.reason_label || cm.reason}
                </span>
                <span className="text-emerald-700 tabular-nums shrink-0">
                  −{money(cm.amount)}
                </span>
              </div>
              <div className="text-[10.5px] text-foreground/55 mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="mono">{cm.code}</span>
                <span>·</span>
                <span>{new Date(cm.issued_at).toLocaleDateString()}</span>
                {!compact && cm.destination === "buyer_wallet" && (
                  <span className="chip bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0">
                    Buyer wallet
                  </span>
                )}
              </div>
              {cm.note && !compact && (
                <div className="text-[11px] text-foreground/65 mt-1 leading-snug">
                  {cm.note}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
